import { Controller } from "@hotwired/stimulus";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";

let ajax;

export default class extends Controller {
  initialize() {
    this.elementHeight = 0;
    this.prevScroll = 0;
    this.translateY = 0;
    this.active = false;
    this.sticky = false;
    this.menu = this.element.querySelector(".menu");
    this.menuInner = this.element.querySelector(".menu__inner");
    this.isFixed = false;

    // iOS-fix: Make sure .menu__inner is the only
    // thing scrolling while menu is open.
    //
    // This fixes the overflow scroll hangup that
    // sometimes happens on iOS when user scrolls
    // to bottom of the overflowing element.
    this.menu.addEventListener("touchmove", (evt) => {
      if (this.menuOpen) {
        evt.preventDefault();
        return false;
      }

      return true;
    });

    this.menuInner.addEventListener("touchmove", (evt) => {
      evt.stopPropagation();
    });

    this.resize();

    import("@fdaciuk/ajax").then((module) => {
      ajax = module.default;
    });
  }

  connect() {
    window.addEventListener("click", this.onWindowClick.bind(this));
    this.megaButtons = this.element.querySelectorAll(".header__item > button");
    this.menuToggle = this.element.querySelector("#nav-ctrl");
    this.form = this.element.querySelector(".form");
    this.input = this.element.querySelector(".form input");
    this.res = this.element.querySelector(".menu__res");
    this.miniHeader = this.element.querySelector(".mini-header");

    if (this.input) {
      let inputTimer;
      this.input.addEventListener("input", (evt) => {
        let s = this.input.value;
        clearTimeout(inputTimer);

        if (s.length < 3) {
          this.res.innerHTML = "";
          this.res.classList.remove("active");
          return;
        }

        inputTimer = setTimeout(() => {
          this.search(s);
        }, 200);
      });

      this.form.addEventListener("submit", (evt) => {
        evt.preventDefault();
      });
    }
  }

  disconnect() {
    window.removeEventListener("click", this.onWindowClick.bind(this));
  }

  onWindowClick(evt) {
    let parentMega = evt.target.closest(".header__mega");
    if (!parentMega) {
      this.closeMegas();
    }
  }

  update() {
    if (this.menuOpen) {
      return true;
    }

    let scroll = window.pageYOffset || document.documentElement.scrollTop;

    if (scroll <= 0) {
      this.prevScroll = 0;
      this.translateY = 0;
      this.element.style = `transform: translateY(-${this.translateY}px);`;

      this.element.classList.remove("header--fixed");
      this.isFixed = false;

      return;
    }

    if (this.active) {
      this.prevScroll = scroll;
      return;
    }

    if (scroll > this.elementHeight && !this.isFixed) {
      this.element.classList.add("header--fixed");
      this.isFixed = true;

      this.prevScroll = 0;
      this.translateY = this.elementHeight;
      this.element.style = `transform: translateY(-${this.translateY}px);`;

      return;
    }

    if (!this.isFixed) {
      return;
    }

    let delta = scroll - this.prevScroll;
    let substractMiniHeader = this.miniHeader
      ? Math.min(scroll, window.innerWidth > 992 ? 40 : 24)
      : 0;

    this.translateY = Math.max(
      Math.min(this.translateY + delta, this.elementHeight),
      0
    );

    this.translateY = Math.max(this.translateY, substractMiniHeader);

    this.element.style = `transform: translateY(-${this.translateY}px);`;

    this.prevScroll = scroll;

    if (scroll > this.elementHeight && !this.sticky) {
      this.element.classList.add("header--sticky");
      this.sticky = true;
    } else if (scroll <= this.elementHeight && this.sticky) {
      this.element.classList.remove("header--sticky");
      this.sticky = false;
    }
  }

  toggleMega(evt) {
    evt.stopImmediatePropagation();
    let target = evt.currentTarget;
    let open = target.classList.contains("open");

    if (!open) {
      this.openMega(target);
    }

    return false;
  }

  openMega(target) {
    target.classList.add("open");
    target.setAttribute("aria-expanded", true);
    let mega = this.element.querySelector(
      "#" + target.getAttribute("aria-controls")
    );
    mega.classList.add("open");
  }

  closeMegas() {
    this.megaButtons.forEach((element) => {
      element.classList.remove("open");
      element.setAttribute("aria-expanded", false);
      let mega = this.element.querySelector(
        "#" + element.getAttribute("aria-controls")
      );
      mega.classList.remove("open");
    });
  }

  resize() {
    this.elementHeight = window.innerWidth > 992 ? 144 : 104;

    if (this.inPageNav) {
      const rect = this.inPageNav.getBoundingClientRect();
      this.inPageNavTop = rect.top + window.scrollY - 80;
    }

    this.update();
  }

  toggleMenu(evt) {
    let open = this.element.classList.contains("menu-open");

    if (!open) {
      this.element.classList.add("menu-open");
      this.menuOpen = true;

      // always hide mini header on fullscreen nav
      // if(this.prevScroll < 40) {
      //   window.scroll(0, 40);
      // }

      disableBodyScroll(this.element);
      this.prevTransform = this.element.style.transform;
      this.element.style = `transform: translateY(0px);`;
      // this.prevScroll = Number.POSITIVE_INFINITY;
      // this.update();

      this.menuToggle.querySelector("use").setAttribute("xlink:href", "#close");
    } else {
      this.menuOpen = false;
      this.element.classList.remove("menu-open");
      enableBodyScroll(this.element);
      this.element.style = `transform: ${this.prevTransform};`;
      this.menuToggle.querySelector("use").setAttribute("xlink:href", "#menu");
      if (this.res) {
        this.res.innerHTML = "";
        this.res.classList.remove("active");
      }
      if (this.input) {
        this.input.value = "";
      }
    }
  }

  search(query) {
    if (!ajax) {
      return;
    }

    this.form.classList.add("loading");

    ajax({
      method: "get",
      url: _ajaxurl, // global
      data: {
        action: "search",
        s: query,
      },
    })
      .then((res) => {
        if (res.length) {
          let html = ``;
          res.forEach((r) => {
            html += `<a href="${r.url}">`;

            if (r.image) {
              html += `<div class="img"><img src="${r.image.sizes["standard-sm"]}" /></div>`;
            }

            html += `<div class="txt">`;
            if (r.type) {
              html += `<div><span class="badge">${r.type}</span></div>`;
            }
            html += `<h2>${r.heading}</h2>`;

            if (r.text) {
              html += `<p>${r.text}</p>`;
            }

            html += `</div></a>`;
          });

          this.res.innerHTML = html;
          this.res.classList.add("active");
        } else {
          this.res.innerHTML = "";
          this.res.classList.remove("active");
        }

        this.form.classList.remove("loading");
      })
      .catch(() => {
        this.form.classList.remove("loading");
      });
  }
}
